// src/provider/provider.ts
import { ServerProviderStatus } from "@openfeature/core";

// src/provider/no-op-provider.ts
var REASON_NO_OP = "No-op";
var NoopFeatureProvider = class {
  metadata = {
    name: "No-op Provider"
  };
  resolveBooleanEvaluation(_, defaultValue) {
    return this.noOp(defaultValue);
  }
  resolveStringEvaluation(_, defaultValue) {
    return this.noOp(defaultValue);
  }
  resolveNumberEvaluation(_, defaultValue) {
    return this.noOp(defaultValue);
  }
  resolveObjectEvaluation(_, defaultValue) {
    return this.noOp(defaultValue);
  }
  noOp(defaultValue) {
    return Promise.resolve({
      value: defaultValue,
      reason: REASON_NO_OP
    });
  }
};
var NOOP_PROVIDER = new NoopFeatureProvider();

// src/provider/in-memory-provider/in-memory-provider.ts
import {
  FlagNotFoundError,
  GeneralError,
  OpenFeatureError as OpenFeatureError2,
  StandardResolutionReasons,
  TypeMismatchError
} from "@openfeature/core";

// src/provider/in-memory-provider/variant-not-found-error.ts
import { ErrorCode, OpenFeatureError } from "@openfeature/core";
var VariantFoundError = class _VariantFoundError extends OpenFeatureError {
  code;
  constructor(message) {
    super(message);
    Object.setPrototypeOf(this, _VariantFoundError.prototype);
    this.name = "VariantFoundError";
    this.code = ErrorCode.GENERAL;
  }
};

// src/provider/in-memory-provider/in-memory-provider.ts
var InMemoryProvider = class {
  events = new OpenFeatureEventEmitter();
  runsOn = "server";
  metadata = {
    name: "in-memory"
  };
  _flagConfiguration;
  constructor(flagConfiguration = {}) {
    this._flagConfiguration = { ...flagConfiguration };
  }
  /**
   * Overwrites the configured flags.
   * @param { FlagConfiguration } flagConfiguration new flag configuration
   */
  putConfiguration(flagConfiguration) {
    const flagsChanged = Object.entries(flagConfiguration).filter(([key, value]) => this._flagConfiguration[key] !== value).map(([key]) => key);
    this._flagConfiguration = { ...flagConfiguration };
    this.events.emit(ServerProviderEvents.ConfigurationChanged, { flagsChanged });
  }
  resolveBooleanEvaluation(flagKey, defaultValue, context, logger) {
    return this.resolveFlagWithReason(flagKey, defaultValue, context, logger);
  }
  resolveNumberEvaluation(flagKey, defaultValue, context, logger) {
    return this.resolveFlagWithReason(flagKey, defaultValue, context, logger);
  }
  async resolveStringEvaluation(flagKey, defaultValue, context, logger) {
    return this.resolveFlagWithReason(flagKey, defaultValue, context, logger);
  }
  async resolveObjectEvaluation(flagKey, defaultValue, context, logger) {
    return this.resolveFlagWithReason(flagKey, defaultValue, context, logger);
  }
  async resolveFlagWithReason(flagKey, defaultValue, ctx, logger) {
    try {
      const resolutionResult = this.lookupFlagValue(flagKey, defaultValue, ctx, logger);
      if (typeof resolutionResult?.value != typeof defaultValue) {
        throw new TypeMismatchError();
      }
      return resolutionResult;
    } catch (error) {
      if (!(error instanceof OpenFeatureError2)) {
        throw new GeneralError(error?.message || "unknown error");
      }
      throw error;
    }
  }
  lookupFlagValue(flagKey, defaultValue, ctx, logger) {
    if (!(flagKey in this._flagConfiguration)) {
      const message = `no flag found with key ${flagKey}`;
      logger?.debug(message);
      throw new FlagNotFoundError(message);
    }
    const flagSpec = this._flagConfiguration[flagKey];
    if (flagSpec.disabled) {
      return { value: defaultValue, reason: StandardResolutionReasons.DISABLED };
    }
    const isContextEval = ctx && flagSpec?.contextEvaluator;
    const variant = isContextEval ? flagSpec.contextEvaluator?.(ctx) : flagSpec.defaultVariant;
    const value = variant && flagSpec?.variants[variant];
    if (value === void 0) {
      const message = `no value associated with variant ${variant}`;
      logger?.error(message);
      throw new VariantFoundError(message);
    }
    return {
      value,
      ...variant && { variant },
      reason: isContextEval ? StandardResolutionReasons.TARGETING_MATCH : StandardResolutionReasons.STATIC
    };
  }
};

// src/open-feature.ts
import {
  OpenFeatureCommonAPI,
  ProviderWrapper,
  objectOrUndefined,
  stringOrUndefined
} from "@openfeature/core";

// src/client/internal/open-feature-client.ts
import {
  ErrorCode as ErrorCode2,
  ProviderFatalError,
  ProviderNotReadyError,
  SafeLogger,
  StandardResolutionReasons as StandardResolutionReasons2,
  instantiateErrorByErrorCode,
  statusMatchesEvent
} from "@openfeature/core";
var OpenFeatureClient = class {
  constructor(providerAccessor, providerStatusAccessor, emitterAccessor, globalLogger, options, context = {}) {
    this.providerAccessor = providerAccessor;
    this.providerStatusAccessor = providerStatusAccessor;
    this.emitterAccessor = emitterAccessor;
    this.globalLogger = globalLogger;
    this.options = options;
    this._context = context;
  }
  _context;
  _hooks = [];
  _clientLogger;
  get metadata() {
    return {
      // Use domain if name is not provided
      name: this.options.domain ?? this.options.name,
      domain: this.options.domain ?? this.options.name,
      version: this.options.version,
      providerMetadata: this.providerAccessor().metadata
    };
  }
  get providerStatus() {
    return this.providerStatusAccessor();
  }
  addHandler(eventType, handler) {
    this.emitterAccessor().addHandler(eventType, handler);
    const shouldRunNow = statusMatchesEvent(eventType, this._providerStatus);
    if (shouldRunNow) {
      try {
        handler({
          clientName: this.metadata.name,
          domain: this.metadata.domain,
          providerName: this._provider.metadata.name
        });
      } catch (err) {
        this._logger?.error("Error running event handler:", err);
      }
    }
  }
  removeHandler(eventType, handler) {
    this.emitterAccessor().removeHandler(eventType, handler);
  }
  getHandlers(eventType) {
    return this.emitterAccessor().getHandlers(eventType);
  }
  setLogger(logger) {
    this._clientLogger = new SafeLogger(logger);
    return this;
  }
  setContext(context) {
    this._context = context;
    return this;
  }
  getContext() {
    return this._context;
  }
  addHooks(...hooks) {
    this._hooks = [...this._hooks, ...hooks];
    return this;
  }
  getHooks() {
    return this._hooks;
  }
  clearHooks() {
    this._hooks = [];
    return this;
  }
  async getBooleanValue(flagKey, defaultValue, context, options) {
    return (await this.getBooleanDetails(flagKey, defaultValue, context, options)).value;
  }
  getBooleanDetails(flagKey, defaultValue, context, options) {
    return this.evaluate(
      flagKey,
      this._provider.resolveBooleanEvaluation,
      defaultValue,
      "boolean",
      context,
      options
    );
  }
  async getStringValue(flagKey, defaultValue, context, options) {
    return (await this.getStringDetails(flagKey, defaultValue, context, options)).value;
  }
  getStringDetails(flagKey, defaultValue, context, options) {
    return this.evaluate(
      flagKey,
      // this isolates providers from our restricted string generic argument.
      this._provider.resolveStringEvaluation,
      defaultValue,
      "string",
      context,
      options
    );
  }
  async getNumberValue(flagKey, defaultValue, context, options) {
    return (await this.getNumberDetails(flagKey, defaultValue, context, options)).value;
  }
  getNumberDetails(flagKey, defaultValue, context, options) {
    return this.evaluate(
      flagKey,
      // this isolates providers from our restricted number generic argument.
      this._provider.resolveNumberEvaluation,
      defaultValue,
      "number",
      context,
      options
    );
  }
  async getObjectValue(flagKey, defaultValue, context, options) {
    return (await this.getObjectDetails(flagKey, defaultValue, context, options)).value;
  }
  getObjectDetails(flagKey, defaultValue, context, options) {
    return this.evaluate(flagKey, this._provider.resolveObjectEvaluation, defaultValue, "object", context, options);
  }
  async evaluate(flagKey, resolver, defaultValue, flagType, invocationContext = {}, options = {}) {
    const allHooks = [
      ...OpenFeature.getHooks(),
      ...this.getHooks(),
      ...options.hooks || [],
      ...this._provider.hooks || []
    ];
    const allHooksReversed = [...allHooks].reverse();
    const mergedContext = {
      ...OpenFeature.getContext(),
      ...OpenFeature.getTransactionContext(),
      ...this._context,
      ...invocationContext
    };
    const hookContext = {
      flagKey,
      defaultValue,
      flagValueType: flagType,
      clientMetadata: this.metadata,
      providerMetadata: this._provider.metadata,
      context: mergedContext,
      logger: this._logger
    };
    try {
      const frozenContext = await this.beforeHooks(allHooks, hookContext, options);
      if (this.providerStatus === ServerProviderStatus.NOT_READY) {
        throw new ProviderNotReadyError("provider has not yet initialized");
      } else if (this.providerStatus === ServerProviderStatus.FATAL) {
        throw new ProviderFatalError("provider is in an irrecoverable error state");
      }
      const resolution = await resolver.call(this._provider, flagKey, defaultValue, frozenContext, this._logger);
      const evaluationDetails = {
        ...resolution,
        flagMetadata: Object.freeze(resolution.flagMetadata ?? {}),
        flagKey
      };
      if (evaluationDetails.errorCode) {
        throw instantiateErrorByErrorCode(evaluationDetails.errorCode);
      }
      await this.afterHooks(allHooksReversed, hookContext, evaluationDetails, options);
      return evaluationDetails;
    } catch (err) {
      const errorMessage = err?.message;
      const errorCode = err?.code || ErrorCode2.GENERAL;
      await this.errorHooks(allHooksReversed, hookContext, err, options);
      return {
        errorCode,
        errorMessage,
        value: defaultValue,
        reason: StandardResolutionReasons2.ERROR,
        flagMetadata: Object.freeze({}),
        flagKey
      };
    } finally {
      await this.finallyHooks(allHooksReversed, hookContext, options);
    }
  }
  async beforeHooks(hooks, hookContext, options) {
    for (const hook of hooks) {
      Object.freeze(hookContext);
      Object.assign(hookContext.context, {
        ...hookContext.context,
        ...await hook?.before?.(hookContext, Object.freeze(options.hookHints))
      });
    }
    return Object.freeze(hookContext.context);
  }
  async afterHooks(hooks, hookContext, evaluationDetails, options) {
    for (const hook of hooks) {
      await hook?.after?.(hookContext, evaluationDetails, options.hookHints);
    }
  }
  async errorHooks(hooks, hookContext, err, options) {
    for (const hook of hooks) {
      try {
        await hook?.error?.(hookContext, err, options.hookHints);
      } catch (err2) {
        this._logger.error(`Unhandled error during 'error' hook: ${err2}`);
        if (err2 instanceof Error) {
          this._logger.error(err2.stack);
        }
        this._logger.error(err2?.stack);
      }
    }
  }
  async finallyHooks(hooks, hookContext, options) {
    for (const hook of hooks) {
      try {
        await hook?.finally?.(hookContext, options.hookHints);
      } catch (err) {
        this._logger.error(`Unhandled error during 'finally' hook: ${err}`);
        if (err instanceof Error) {
          this._logger.error(err.stack);
        }
        this._logger.error(err?.stack);
      }
    }
  }
  get _provider() {
    return this.providerAccessor();
  }
  get _providerStatus() {
    return this.providerStatusAccessor();
  }
  get _logger() {
    return this._clientLogger || this.globalLogger();
  }
};

// src/events/open-feature-event-emitter.ts
import { GenericEventEmitter } from "@openfeature/core";
import { EventEmitter } from "events";
var OpenFeatureEventEmitter = class extends GenericEventEmitter {
  eventEmitter = new EventEmitter({ captureRejections: true });
  constructor() {
    super();
    this.eventEmitter.on("error", (err) => {
      this._logger?.error("Error running event handler:", err);
    });
  }
};

// src/events/events.ts
import { ServerProviderEvents } from "@openfeature/core";

// src/transaction-context/no-op-transaction-context-propagator.ts
var NoopTransactionContextPropagator = class {
  getTransactionContext() {
    return {};
  }
  setTransactionContext(_, callback, ...args) {
    callback(...args);
  }
};
var NOOP_TRANSACTION_CONTEXT_PROPAGATOR = new NoopTransactionContextPropagator();

// src/transaction-context/async-local-storage-transaction-context-propagator.ts
import { AsyncLocalStorage } from "async_hooks";
var AsyncLocalStorageTransactionContextPropagator = class {
  asyncLocalStorage = new AsyncLocalStorage();
  getTransactionContext() {
    return this.asyncLocalStorage.getStore() ?? {};
  }
  setTransactionContext(transactionContext, callback, ...args) {
    this.asyncLocalStorage.run(transactionContext, callback, ...args);
  }
};

// src/open-feature.ts
var GLOBAL_OPENFEATURE_API_KEY = Symbol.for("@openfeature/js-sdk/api");
var _globalThis = globalThis;
var OpenFeatureAPI = class _OpenFeatureAPI extends OpenFeatureCommonAPI {
  _statusEnumType = ServerProviderStatus;
  _apiEmitter = new OpenFeatureEventEmitter();
  _defaultProvider = new ProviderWrapper(
    NOOP_PROVIDER,
    ServerProviderStatus.NOT_READY,
    this._statusEnumType
  );
  _domainScopedProviders = /* @__PURE__ */ new Map();
  _createEventEmitter = () => new OpenFeatureEventEmitter();
  _transactionContextPropagator = NOOP_TRANSACTION_CONTEXT_PROPAGATOR;
  constructor() {
    super("server");
  }
  /**
   * Gets a singleton instance of the OpenFeature API.
   * @ignore
   * @returns {OpenFeatureAPI} OpenFeature API
   */
  static getInstance() {
    const globalApi = _globalThis[GLOBAL_OPENFEATURE_API_KEY];
    if (globalApi) {
      return globalApi;
    }
    const instance = new _OpenFeatureAPI();
    _globalThis[GLOBAL_OPENFEATURE_API_KEY] = instance;
    return instance;
  }
  getProviderStatus(domain) {
    if (!domain) {
      return this._defaultProvider.status;
    }
    return this._domainScopedProviders.get(domain)?.status ?? this._defaultProvider.status;
  }
  async setProviderAndWait(domainOrProvider, providerOrUndefined) {
    const domain = stringOrUndefined(domainOrProvider);
    const provider = domain ? objectOrUndefined(providerOrUndefined) : objectOrUndefined(domainOrProvider);
    await this.setAwaitableProvider(domain, provider);
  }
  setProvider(clientOrProvider, providerOrUndefined) {
    const domain = stringOrUndefined(clientOrProvider);
    const provider = domain ? objectOrUndefined(providerOrUndefined) : objectOrUndefined(clientOrProvider);
    const maybePromise = this.setAwaitableProvider(domain, provider);
    Promise.resolve(maybePromise).catch((err) => {
      this._logger.error("Error during provider initialization:", err);
    });
    return this;
  }
  setContext(context) {
    this._context = context;
    return this;
  }
  getContext() {
    return this._context;
  }
  getClient(domainOrContext, versionOrContext, contextOrUndefined) {
    const domain = stringOrUndefined(domainOrContext);
    const version = stringOrUndefined(versionOrContext);
    const context = objectOrUndefined(domainOrContext) ?? objectOrUndefined(versionOrContext) ?? objectOrUndefined(contextOrUndefined);
    return new OpenFeatureClient(
      () => this.getProviderForClient(domain),
      () => this.getProviderStatus(domain),
      () => this.buildAndCacheEventEmitterForClient(domain),
      () => this._logger,
      { domain, version },
      context
    );
  }
  /**
   * Clears all registered providers and resets the default provider.
   * @returns {Promise<void>}
   */
  clearProviders() {
    return super.clearProvidersAndSetDefault(NOOP_PROVIDER);
  }
  setTransactionContextPropagator(transactionContextPropagator) {
    const baseMessage = "Invalid TransactionContextPropagator, will not be set: ";
    if (typeof transactionContextPropagator?.getTransactionContext !== "function") {
      this._logger.error(`${baseMessage}: getTransactionContext is not a function.`);
    } else if (typeof transactionContextPropagator?.setTransactionContext !== "function") {
      this._logger.error(`${baseMessage}: setTransactionContext is not a function.`);
    } else {
      this._transactionContextPropagator = transactionContextPropagator;
    }
    return this;
  }
  setTransactionContext(transactionContext, callback, ...args) {
    this._transactionContextPropagator.setTransactionContext(transactionContext, callback, ...args);
  }
  getTransactionContext() {
    try {
      return this._transactionContextPropagator.getTransactionContext();
    } catch (err) {
      const error = err;
      this._logger.error(`Error getting transaction context: ${error?.message}, returning empty context.`);
      this._logger.error(error?.stack);
      return {};
    }
  }
};
var OpenFeature = OpenFeatureAPI.getInstance();

// src/index.ts
export * from "@openfeature/core";
export {
  AsyncLocalStorageTransactionContextPropagator,
  InMemoryProvider,
  NOOP_PROVIDER,
  NOOP_TRANSACTION_CONTEXT_PROPAGATOR,
  OpenFeature,
  OpenFeatureAPI,
  OpenFeatureEventEmitter,
  ServerProviderEvents as ProviderEvents,
  ServerProviderStatus as ProviderStatus
};
//# sourceMappingURL=index.js.map
